import React from 'react';
import {
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { isFailedArticleState } from '../../../../pages/Articles/articleStatus';
import Spinner from './Spinner';

/**
 * Back / generate / next buttons under the wizard.
 * Extracted verbatim from pages/Articles/CreateArticle.js.
 */
export default function WizardFooter({
  articleData,
  articleType,
  currentStep,
  goToNextStep,
  goToPreviousStep,
  handleContinue,
  handleSubmit,
  isDraftGenerated,
  loading,
  numberOfArticles,
}) {
  const { t } = useTranslation();

  return (
    <div className="mt-4 border-t border-gray-200 pt-8">
      <div className="mx-auto flex justify-between space-x-4">
        {/* Back button on the left */}
        <div>
          {currentStep > 0 && (
            <button
              onMouseDown={(e) => e.preventDefault()}
              onClick={goToPreviousStep}
              className="inline-flex items-center justify-center rounded-full px-6 py-3 text-base bg-gray-100 text-gray-700 hover:bg-gray-200 shadow-sm focus:outline-none gap-2"
            >
              <ArrowLeftIcon className="w-5 h-5" /> {t('common.back')}
            </button>
          )}
        </div>

        {/* Action buttons on the right */}
        <div className="flex space-x-4">
          {/* Standardized flow: generate draft on step 2 for all single content types */}
          {articleType === 'single' && currentStep === 2 && (
            <button
              type="submit"
              disabled={loading}
              onClick={handleSubmit}
              className={`inline-flex items-center justify-center rounded-full px-6 py-3 text-base shadow-sm focus:outline-none focus:ring-2 ${!loading
                ? 'bg-primary-500 hover:bg-primary-600 cursor-pointer focus:ring-primary-500 focus:ring-offset-2 text-white'
                : 'bg-gray-300 cursor-not-allowed text-gray-500'
                }`}
            >
              {loading ? (
                <>
                  <Spinner className="w-4 h-4 mr-2" />
                  {t('articles.create.actions.generating')}
                </>
              ) : (
                t('articles.create.actions.createDraft')
              )}
            </button>
          )}

          {/* For bulk articles: show bulk generation button on step 4 */}
          {articleType === 'bulk' && currentStep === 4 && (
            <button
              type="submit"
              disabled={loading}
              onClick={handleSubmit}
              className={`inline-flex items-center justify-center rounded-full px-6 py-3 text-base shadow-sm focus:outline-none focus:ring-2 ${!loading
                ? 'bg-primary-500 hover:bg-primary-600 cursor-pointer focus:ring-primary-500 focus:ring-offset-2 text-white'
                : 'bg-gray-300 cursor-not-allowed text-gray-500'
                }`}
            >
              {loading
                ? t('articles.create.actions.generating')
                : t('articles.create.actions.generateArticles', { count: numberOfArticles })}
            </button>
          )}

          {/* Step 4: final article generation from existing draft */}
          {articleType === 'single' && currentStep === 5 && (
            <>
              {isDraftGenerated && (
                <button
                  onClick={() => {
                    handleContinue();
                  }}
                  disabled={loading}
                  className={`inline-flex items-center justify-center rounded-full px-6 py-3 text-base text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${!loading
                    ? 'bg-primary-500 hover:bg-primary-600 focus:ring-primary-500'
                    : 'bg-gray-300 cursor-not-allowed'
                    }`}
                >
                  {isFailedArticleState(articleData)
                    ? t('articles.create.actions.generateAgain')
                    : t('articles.create.actions.generate')}
                </button>
              )}
            </>
          )}

          {/* Next step button for steps before the final action step */}
          {((articleType === 'single' && (currentStep === 1 || currentStep === 3 || currentStep === 4 || (currentStep === 2 && isDraftGenerated))) || (articleType === 'bulk' && currentStep < 4)) && (
            <button
              onMouseDown={(e) => e.preventDefault()}
              onClick={goToNextStep}
              className="inline-flex items-center justify-center rounded-full px-6 py-3 text-base bg-primary-500 hover:bg-primary-600 text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              {t('articles.create.navigation.nextStep')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
